import { FloatingFormItem, InputDecimal } from '@/components/_common';
import { TaxePisCstSelect } from '@/components/_common/TaxesSelect';
import { Card, Row, Col, FormInstance, Form } from 'antd';
import { InvoicePartFormValues } from '../InvoicePartForm.types';
import { useInvoiceFormContext } from '../../../InvoiceFormProvider';
import { PisCofinsCst } from '@/core/domain/invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import { useCallback } from 'react';

interface Props {
  form: FormInstance<InvoicePartFormValues>;
}

const InvoicePartPisForm: React.FC<Props> = ({ form }) => {
  const { disabledFields } = useInvoiceFormContext();

  const pisCst = Form.useWatch(['itemTaxes', 'pis', 'pisCst'], form);

  const hasCstMonophasic = pisCst === PisCofinsCst.TAXABLE_OPERATION_MONOPHASIC;

  const handleCst = useCallback(
    (cstValue: PisCofinsCst) => {
      if (cstValue === PisCofinsCst.TAXABLE_OPERATION_MONOPHASIC) {
        form.setFieldsValue({
          itemTaxes: {
            pis: {
              pisAliquot: undefined,
              pisValue: undefined,
              pisBaseCalculationValue: undefined,
              pisSoldQuantity: undefined,
              pisRealAliquot: undefined,
              pisStAliquot: undefined,
              pisStValue: undefined,
              pisStBaseCalculation: undefined,
              pisStSoldQuantity: undefined,
              pisStRealAliquot: undefined,
            },
          },
        });
      }
    },
    [form],
  );

  return (
    <Form form={form} preserve={false} disabled={disabledFields}>
      <Card title="PIS" type="inner" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <FloatingFormItem
              label="CST"
              name={['itemTaxes', 'pis', 'pisCst']}
              getValueFromEvent={(value) => {
                handleCst(value);
                return value;
              }}
            >
              <TaxePisCstSelect allowClear />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Alíquota"
              name={['itemTaxes', 'pis', 'pisAliquot']}
            >
              <InputDecimal fractions={4} max={100} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Valor"
              name={['itemTaxes', 'pis', 'pisValue']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Base de cálculo"
              name={['itemTaxes', 'pis', 'pisBaseCalculationValue']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Quantidade vendida"
              name={['itemTaxes', 'pis', 'pisSoldQuantity']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={4}>
            <FloatingFormItem
              label="Alíquota em reais"
              name={['itemTaxes', 'pis', 'pisRealAliquot']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>

      <Card title="PIS ST" type="inner">
        <Row gutter={[16, 16]}>
          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Alíquota"
              name={['itemTaxes', 'pis', 'pisStAliquot']}
            >
              <InputDecimal
                fractions={4}
                max={100}
                disabled={hasCstMonophasic}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Valor"
              name={['itemTaxes', 'pis', 'pisStValue']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Base de cálculo"
              name={['itemTaxes', 'pis', 'pisStBaseCalculation']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={5}>
            <FloatingFormItem
              label="Quantidade vendida"
              name={['itemTaxes', 'pis', 'pisStSoldQuantity']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>

          <Col span={24} sm={12} md={8} lg={4}>
            <FloatingFormItem
              label="Alíquota em reais"
              name={['itemTaxes', 'pis', 'pisStRealAliquot']}
            >
              <InputDecimal disabled={hasCstMonophasic} />
            </FloatingFormItem>
          </Col>
        </Row>
      </Card>
    </Form>
  );
};

export default InvoicePartPisForm;
